import { computed, ComputedRef, inject } from 'vue';
import usePortfolio from '../../usePortfolio';
import { IChartInterface } from './ChartInterface';
import {
  getAddressFromAsset,
  getAssetsFromElement,
  getImageFromAsset,
  getLabelFromAsset,
  NetworkIdType,
  PortfolioAsset,
  TokenInfo,
} from '@sonarwatch/portfolio-core';
import { useDataStore } from '@/store/data';
import { unknownImageUrl } from '@/constants';
import { handleOther } from './helpers';

type Dataset = {
  name: string;
  value: number;
  pourcent: number;
};

export default function useAssetsData(): IChartInterface {
  const { portfolioInfo } = usePortfolio();
  const dataStore = useDataStore();

  const assets = computed(() => {
    const data = getAssetsData(
      portfolioInfo.assetWalletTokens.value,
      portfolioInfo.elementsByPlatformId.value,
      dataStore.getTokenInfo,
    );
    return data;
  });
  const total = computed(() => {
    return assets.value.reduce((acc, asset) => acc + asset.value, 0);
  });

  const dataset: ComputedRef<Dataset[]> = computed(() => {
    const ds = assets.value
      .map((asset) => ({
        name: asset.name,
        value: asset.value,
        pourcent: (asset.value / total.value) * 100,
      }))
      .sort((a, b) => b.value - a.value);
    return handleOther(ds, 5, total.value);
  });

  return {
    dataset,
    total,
  };
}

function getAssetsData(
  assets: PortfolioAsset[],
  elementsByPlatformId: any,
  getTokenInfo: (
    address: string | null,
    networkId: NetworkIdType,
  ) => TokenInfo | undefined,
): { name: string; value: number }[] {
  let hashmap: Map<
    string,
    { value: number; image: string; tokenAddresses: string[]; label: string }
  > = new Map();

  const array = [...assets];

  array.forEach((item: any) => {
    const address = getAddressFromAsset(item);
    const tokenInfo = getTokenInfo(address!, item.networkId);

    const label = getLabelFromAsset(item, tokenInfo) ?? '';
    const value = item.value as number;
    const image = getImageFromAsset(item, tokenInfo);

    const acc = hashmap.get(label)?.value ?? 0;
    const array = hashmap.get(label)?.tokenAddresses ?? [];
    hashmap.set(label, {
      value: acc + value,
      image: image || unknownImageUrl,
      tokenAddresses: [...array, item.data.address],
      label,
    });
  });

  elementsByPlatformId.forEach((p: any) => {
    p.elements.forEach((pe: any) => {
      // Dont count elements with borrow position
      if (pe.type === 'borrowlend') {
        if (pe.data?.borrowedValue ?? 0 > 0) {
          return;
        }
      }
      getAssetsFromElement(pe).forEach((e: any) => {
        const address = getAddressFromAsset(e);
        const tokenInfo = getTokenInfo(address, e.networkId);
        let label = getLabelFromAsset(e, tokenInfo) ?? '';
        if (p.platformId === 'native-stake') {
          label = 'SOL';
        }
        const value = e.value as number;
        const acc = hashmap.get(label)?.value ?? 0;
        const array = hashmap.get(label)?.tokenAddresses ?? [];
        const image = getImageFromAsset(e, tokenInfo);
        hashmap.set(label, {
          value: acc + value,
          image: image || unknownImageUrl,
          tokenAddresses: [...array, e.data.address],
          label,
        });
      });
    });
  });

  return Array.from(hashmap.values()).map((e) => ({
    name: e.label,
    value: e.value,
  }));
}
